import { Component, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  Subscription,
  finalize,
  switchMap,
  tap,
  map,
  throwError
} from 'rxjs';

import { TargetAudience } from '../models/target-audience';
import { TargetAudienceLocation } from '../models/target-audience-location';
import { CollectDataService } from '../services/collect-data.service';
import { UiStateService } from '../services/ui-state.service';

interface GenerationMessage {
  audienceId: number;
  text: string;
  isError: boolean;
}

@Component({
  selector: 'app-audience-list',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './audience-list.component.html',
  styleUrl: './audience-list.component.css'
})
export class AudienceListComponent implements OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly collectDataService = inject(CollectDataService);
  private readonly uiState = inject(UiStateService);

  private subscription: Subscription | null = null;
  private readonly locationFetches = new Map<number, Subscription>();

  protected readonly serviceId = signal<number | null>(null);
  protected readonly audiences = signal<TargetAudience[]>([]);
  protected readonly loading = signal<boolean>(true);
  protected readonly errorMessage = signal<string>('');
  protected readonly statusMessage = signal<string>('');

  protected readonly generationPending = signal<number | null>(null);
  protected readonly generationMessage = signal<GenerationMessage | null>(null);
  protected readonly bulkGenerationPending = signal<boolean>(false);
  protected readonly viewAllPending = signal<boolean>(false);

  private readonly locationIndex = signal<
    Record<number, Record<number, TargetAudienceLocation[]>>
  >(
    {}
  );

  constructor() {
    this.subscription = this.route.paramMap.subscribe((params) => {
      const serviceParam = params.get('serviceId');
      const parsedServiceId = serviceParam ? Number(serviceParam) : NaN;

      if (Number.isNaN(parsedServiceId)) {
        this.errorMessage.set('Invalid service identifier.');
        this.loading.set(false);
        return;
      }

      this.resetLocationCache();
      this.serviceId.set(parsedServiceId);
      this.uiState.setServiceId(parsedServiceId);
      this.loadAudiences(parsedServiceId);
      this.refreshLocations(parsedServiceId);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.clearLocationFetches();
  }

  protected viewDetails(audience: TargetAudience): void {
    const serviceId = this.serviceId();
    if (!serviceId) {
      return;
    }
    this.uiState.setAudienceId(audience.id);
    this.uiState.setServiceId(serviceId);
    this.router.navigate(['/audiences', serviceId, audience.id]);
  }

  protected viewTargetAreas(audience: TargetAudience): void {
    const serviceId = this.resolveTargetAreaServiceId(audience);
    if (!serviceId) {
      return;
    }
    this.navigateToTargetAreas(serviceId, audience.id);
  }

  protected targetAreaActionLabel(audience: TargetAudience): string {
    return this.hasGeneratedTargetArea(audience)
      ? 'View Target Area'
      : 'Generate Target Area';
  }

  protected isGenerating(audience: TargetAudience): boolean {
    return this.generationPending() === audience.id;
  }

  protected viewAllGeneratedTargetAreas(): void {
    const serviceId = this.serviceId();
    if (!serviceId || this.bulkGenerationPending() || this.viewAllPending()) {
      return;
    }

    this.viewAllPending.set(true);
    this.errorMessage.set('');
    this.statusMessage.set('Fetching generated target areas...');

    this.collectDataService
      .getTargetAreaSummary(serviceId)
      .pipe(
        finalize(() => {
          this.viewAllPending.set(false);
        })
      )
      .subscribe({
        next: (summaries) => {
          const generatedSummaries = (summaries ?? []).filter(
            (summary) => (summary.total_locations ?? 0) > 0
          );

          if (!generatedSummaries.length) {
            this.statusMessage.set(
              'No target areas are available yet. Generate target areas to view them.'
            );
            return;
          }

          generatedSummaries.forEach((summary) => {
            const id = Number(summary.audience?.id);
            if (serviceId && Number.isFinite(id) && id > 0) {
              this.uiState.markTargetAreaGenerated(serviceId, id);
            }
          });

          const firstAudienceId = Number(generatedSummaries[0]?.audience?.id);
          const validAudienceId = Number.isFinite(firstAudienceId)
            ? firstAudienceId
            : null;
          this.statusMessage.set('');
          this.errorMessage.set('');
          this.navigateToTargetAreas(serviceId, validAudienceId);
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to load generated target areas. Please try again.';
          this.errorMessage.set(message);
          this.statusMessage.set('');
        }
      });
  }

  protected handleTargetAreaAction(audience: TargetAudience): void {
    if (this.bulkGenerationPending() || this.isGenerating(audience)) {
      return;
    }
    if (this.hasGeneratedTargetArea(audience)) {
      this.viewTargetAreas(audience);
      return;
    }
    this.generateTargetArea(audience);
  }

  protected generateTargetArea(audience: TargetAudience): void {
    const serviceId = this.resolveTargetAreaServiceId(audience);
    if (!serviceId) {
      this.errorMessage.set(
        'Missing service identifier for the selected audience.'
      );
      return;
    }
    if (this.bulkGenerationPending()) {
      return;
    }

    this.generationMessage.set(null);
    this.generationPending.set(audience.id);

    this.collectDataService
      .createTargetAudienceLocationsForAudience(serviceId, audience.id)
      .pipe(
        switchMap((result) => {
          const isSuccess = result?.status?.toLowerCase() === 'success';
          if (!isSuccess) {
            const message =
              result?.message ||
              'Target area generation did not complete. Please try again.';
            return throwError(() => new Error(message));
          }

          return this.collectDataService
            .getLocationsByService(serviceId)
            .pipe(
              tap((locations) => {
                this.updateLocationIndex(locations, serviceId);
              }),
              map((locations) => ({ result, locations }))
            );
        })
      )
      .subscribe({
        next: ({ result }) => {
          this.generationMessage.set({
            audienceId: audience.id,
            text:
              result?.message ||
              'Target areas generated successfully. Select "View Target Area" to explore details.',
            isError: false
          });
          this.generationPending.set(null);
          this.uiState.markTargetAreaGenerated(serviceId, audience.id);
          this.viewTargetAreas(audience);
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to generate target areas. Please try again.';
          this.generationMessage.set({
            audienceId: audience.id,
            text: message,
            isError: true
          });
          this.generationPending.set(null);
        }
      });
  }

  protected hasPendingGenerations(): boolean {
    return this.collectPendingAudienceIds().length > 0;
  }

  protected generateAllTargetAreas(): void {
    const serviceId = this.serviceId();
    if (!serviceId || this.bulkGenerationPending()) {
      return;
    }

    const pendingAudienceIds = this.collectPendingAudienceIds();
    if (!pendingAudienceIds.length) {
      this.errorMessage.set('');
      this.statusMessage.set('All target areas are already generated.');
      return;
    }

    this.bulkGenerationPending.set(true);
    this.generationMessage.set(null);
    this.errorMessage.set('');
    this.statusMessage.set('Generating target areas for all audiences...');

    this.collectDataService
      .createTargetAudienceLocations(serviceId, pendingAudienceIds)
      .pipe(
        switchMap((result) => {
          const isSuccess = result?.status?.toLowerCase() === 'success';
          if (!isSuccess) {
            const message =
              result?.message ||
              'Target area generation did not complete. Please try again.';
            return throwError(() => new Error(message));
          }

          return this.collectDataService.getLocationsByService(serviceId).pipe(
            tap((locations) => {
              this.updateLocationIndex(locations, serviceId);
            }),
            map((locations) => {
              const pendingSet = new Set(pendingAudienceIds);
              const generatedAudienceIds = new Set<number>();
              locations.forEach((location) => {
                const audienceId = Number(location.target_audience_id);
                if (
                  !Number.isNaN(audienceId) &&
                  audienceId > 0 &&
                  pendingSet.has(audienceId)
                ) {
                  generatedAudienceIds.add(audienceId);
                }
              });
              return { result, locations, generatedAudienceIds };
            })
          );
        }),
        finalize(() => {
          this.bulkGenerationPending.set(false);
        })
      )
      .subscribe({
        next: ({ result, generatedAudienceIds }) => {
          const pendingSet = new Set(pendingAudienceIds);

          generatedAudienceIds.forEach((audienceId) => {
            if (pendingSet.has(audienceId)) {
              this.uiState.markTargetAreaGenerated(serviceId, audienceId);
            }
          });

          const affectedCount = Array.from(generatedAudienceIds).filter((id) =>
            pendingSet.has(id)
          ).length;

          this.statusMessage.set(
            result?.message ||
              (affectedCount
                ? `Target areas generated successfully for ${affectedCount} audience(s).`
                : 'No new target areas were generated for the remaining audiences.')
          );

          if (generatedAudienceIds.size) {
            const [firstAudienceId] = Array.from(generatedAudienceIds);
            const activeServiceId = this.serviceId() ?? serviceId;
            if (activeServiceId) {
              this.uiState.setServiceId(activeServiceId);
            }
            this.uiState.setAudienceId(firstAudienceId);
            this.router.navigate(['/target-areas', activeServiceId ?? serviceId], {
              queryParams: { audienceId: firstAudienceId }
            });
          }
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to generate target areas. Please try again.';
          this.statusMessage.set('');
          this.errorMessage.set(message);
        }
      });
  }

  protected generationInfo(audienceId: number): GenerationMessage | null {
    const message = this.generationMessage();
    return message && message.audienceId === audienceId ? message : null;
  }

  protected renderArray(value: unknown): string[] {
    if (value == null) {
      return [];
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (item == null) {
            return '';
          }
          if (typeof item === 'string') {
            return item.trim();
          }
          if (typeof item === 'number' || typeof item === 'boolean') {
            return String(item);
          }
          if (item instanceof Date) {
            return item.toISOString();
          }
          try {
            return JSON.stringify(item);
          } catch {
            return '';
          }
        })
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return this.renderArray(parsed);
        }
      } catch {
        // treat as plain string
      }

      return [trimmed];
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return [String(value)];
    }

    return [];
  }

  protected renderText(value: unknown): string {
    const items = this.renderArray(value);
    if (items.length) {
      return items.join(', ');
    }

    if (value && typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return 'N/A';
      }
    }

    return 'N/A';
  }

  private collectPendingAudienceIds(): number[] {
    const audienceList = this.audiences();
    if (!Array.isArray(audienceList) || !audienceList.length) {
      return [];
    }

    const pending: number[] = [];

    audienceList.forEach((audience) => {
      const numericId = Number(audience?.id);
      if (!Number.isFinite(numericId) || numericId <= 0) {
        return;
      }
      if (this.isGenerating(audience)) {
        return;
      }
      if (!this.hasGeneratedTargetArea(audience)) {
        pending.push(numericId);
      }
    });

    return pending;
  }

  private navigateToTargetAreas(
    serviceId: number,
    audienceId?: number | null
  ): void {
    this.uiState.setServiceId(serviceId);
    this.uiState.setAudienceId(audienceId ?? null);
    const queryParams = audienceId != null ? { audienceId } : undefined;
    this.router.navigate(['/target-areas', serviceId], {
      queryParams
    });
  }

  private loadAudiences(serviceId: number): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.statusMessage.set('Loading target audience profiles...');

    this.collectDataService
      .getTargetAudiences(serviceId)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (audiences) => {
          this.audiences.set(audiences);
          this.statusMessage.set(
            audiences.length
              ? `Loaded ${audiences.length} target audience profile(s).`
              : 'No target audience profiles are available yet.'
          );
          this.uiState.setAudienceId(
            audiences.length ? audiences[0].id : null
          );
          this.prefetchLocationsFromAudiences(audiences);
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to load target audience profiles.';
          this.errorMessage.set(message);
          this.statusMessage.set('');
          this.audiences.set([]);
        }
      });
  }

  private refreshLocations(serviceId: number): void {
    this.queueLocationFetch(serviceId, true);
  }

  private prefetchLocationsFromAudiences(
    audiences: TargetAudience[]
  ): void {
    const uniqueServiceIds = new Set<number>();
    audiences.forEach((audience) => {
      const targetServiceId = this.resolveTargetAreaServiceId(audience);
      if (targetServiceId) {
        uniqueServiceIds.add(targetServiceId);
      }
    });
    uniqueServiceIds.forEach((serviceId) => this.queueLocationFetch(serviceId));
  }

  private queueLocationFetch(
    serviceId: number | null,
    force = false
  ): void {
    const numericId = Number(serviceId);
    if (!numericId || Number.isNaN(numericId)) {
      return;
    }

    if (!force && this.locationFetches.has(numericId)) {
      return;
    }

    if (force) {
      this.locationFetches.get(numericId)?.unsubscribe();
      this.locationFetches.delete(numericId);
    }

    const subscription = this.collectDataService
      .getLocationsByService(numericId)
      .subscribe({
        next: (locations) => {
          this.updateLocationIndex(locations, numericId);
        },
        error: () => {
          // gracefully treat missing data as empty so buttons fall back to "Generate"
          this.updateLocationIndex([], numericId);
          this.locationFetches.delete(numericId);
        },
        complete: () => {
          this.locationFetches.delete(numericId);
        }
      });

    this.locationFetches.set(numericId, subscription);
  }

  private resetLocationCache(): void {
    this.clearLocationFetches();
    this.locationIndex.set({});
  }

  private clearLocationFetches(): void {
    this.locationFetches.forEach((subscription) => subscription.unsubscribe());
    this.locationFetches.clear();
  }

  private resolveTargetAreaServiceId(
    audience?: TargetAudience | null
  ): number | null {
    const audienceServiceId = Number(audience?.service_collected_id);
    if (!Number.isNaN(audienceServiceId) && audienceServiceId > 0) {
      return audienceServiceId;
    }
    const fallback = this.serviceId();
    return fallback ?? null;
  }

  private updateLocationIndex(
    locations: TargetAudienceLocation[],
    serviceId: number
  ): void {
    const current = this.locationIndex();
    const next: Record<
      number,
      Record<number, TargetAudienceLocation[]>
    > = { ...current };

    const serviceKey = Number(serviceId);
    if (Number.isNaN(serviceKey) || serviceKey <= 0) {
      return;
    }

    const updated: Record<number, TargetAudienceLocation[]> = {};
    const generatedAudienceIds = new Set<number>();

    locations.forEach((location) => {
      const audienceKey = Number(location.target_audience_id);
      if (Number.isNaN(audienceKey) || audienceKey <= 0) {
        return;
      }
      const existing = updated[audienceKey] ?? [];
      const deduped = existing.filter((item) => item.id !== location.id);
      const combined = [...deduped, location];
      updated[audienceKey] = combined;
      if (combined.length) {
        generatedAudienceIds.add(audienceKey);
      }
    });

    const previous = current[serviceKey] ?? {};
    // Only clear stale flags; generation status toggles explicitly per row.
    Object.keys(previous).forEach((audienceKey) => {
      const numericKey = Number(audienceKey);
      if (Number.isNaN(numericKey) || numericKey <= 0) {
        return;
      }
      if (!generatedAudienceIds.has(numericKey)) {
        this.uiState.unmarkTargetAreaGenerated(serviceKey, numericKey);
      }
    });

    generatedAudienceIds.forEach((audienceKey) => {
      this.uiState.markTargetAreaGenerated(serviceKey, audienceKey);
    });

    next[serviceKey] = updated;
    this.locationIndex.set(next);
  }

  private hasGeneratedTargetArea(audience: TargetAudience): boolean {
    const serviceId = this.resolveTargetAreaServiceId(audience);
    const audienceId = Number(audience.id);

    if (!serviceId || Number.isNaN(audienceId)) {
      return false;
    }

    return this.uiState.isTargetAreaGenerated(serviceId, audienceId);
  }
}




